/**
 * Main App Component
 * Sets up routing, providers, and accessibility testing
 */

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider, useAuth } from './context/AuthContext';
import { AccountProvider } from './context/AccountContext';
import { OrgProvider } from './context/OrgContext';
import { UpgradeModalProvider } from './context/UpgradeModalContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import FeatureGate from './components/common/FeatureGate';
import DemoBanner from './components/common/DemoBanner';
import StagingBanner from './components/common/StagingBanner';

// Lazy load pages for code splitting
const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const SignupForm = lazy(() => import('./components/auth/SignupForm'));
const DemoBootstrap = lazy(() => import('./demo/DemoBootstrap'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const Campaigns = lazy(() => import('./components/campaigns/CampaignsList'));
const CampaignDetail = lazy(() => import('./components/campaigns/CampaignDetail'));
const Settings = lazy(() => import('./components/settings/Settings'));

// New Feature Pages
const AdCopyGenerator = lazy(() => import('./components/ai/AdCopyGenerator'));
const AnomalyDetection = lazy(() => import('./components/ai/AnomalyDetection'));
const ReportBuilder = lazy(() => import('./components/reports/ReportBuilder'));
const IntegrationsHub = lazy(() => import('./components/integrations/IntegrationsHub'));
const SecuritySettings = lazy(() => import('./components/settings/SecuritySettings'));

// Phase 2 Pages — Onboarding V2
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizardV2'));
const RuleList = lazy(() => import('./components/rules/RuleList'));

// Phase 4 — AI pages
const WeeklyReportPage = lazy(() => import('./pages/WeeklyReportPage'));

// Multi-channel unified dashboard — consolidated into main /dashboard

// Channel OAuth callbacks (Meta Ads & TikTok Ads)
const OAuthCallbackPage = lazy(() => import('./components/channels/OAuthCallbackPage'));

// Phase 3 — Marketing pages
const MarketingLayout = lazy(() => import('./components/marketing/MarketingLayout'));
const LandingPage = lazy(() => import('./pages/marketing/LandingPage'));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage'));

// SEO Calculators (public, under MarketingLayout)
const CalculatorsIndex = lazy(() => import('./pages/calculators/CalculatorsIndex'));
const CalculatorPage   = lazy(() => import('./pages/calculators/CalculatorPage'));

// Competitor comparison pages (public, under MarketingLayout)
const ComparisonIndex = lazy(() => import('./pages/competitors/ComparisonIndex'));
const ComparisonPage  = lazy(() => import('./pages/competitors/ComparisonPage'));

// Free audit tool (public, under MarketingLayout)
const AuditPage        = lazy(() => import('./pages/audit/AuditPage'));
const AuditResultsPage = lazy(() => import('./pages/audit/AuditResultsPage'));

// SEO feature landing pages (public, under MarketingLayout)
const FeaturesIndex  = lazy(() => import('./pages/features/FeaturesIndex'));
const SeoLandingPage = lazy(() => import('./pages/features/SeoLandingPage'));

// Public benchmark pages (SEO traffic)
const GoogleAdsBenchmarksPage = lazy(() => import('./pages/benchmarks/GoogleAdsBenchmarksPage'));
const RoasBenchmarksPage      = lazy(() => import('./pages/benchmarks/RoasBenchmarksPage'));
const CpaBenchmarksPage       = lazy(() => import('./pages/benchmarks/CpaBenchmarksPage'));

// Shared report page (public, no auth)
const SharedReportPage = lazy(() => import('./pages/reports/SharedReportPage'));

// Case studies (public, under MarketingLayout)
const CaseStudiesPage = lazy(() => import('./pages/marketing/CaseStudiesPage'));

// Referral program (protected)
const ReferralPage = lazy(() => import('./pages/referral/ReferralPage'));

// Agency partner program (protected)
const PartnerDashboardPage = lazy(() => import('./pages/partner/PartnerDashboardPage'));
const PartnerApplyPage     = lazy(() => import('./pages/partner/PartnerApplyPage'));

// Billing settings (protected)
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));

// Team management (protected)
const TeamPage = lazy(() => import('./pages/team/TeamPage'));

// Optimization score (protected)
const OptimizationScorePage = lazy(() => import('./pages/optimization/OptimizationScorePage'));

// Campaign Optimizer (protected)
const CampaignOptimizerPage = lazy(() => import('./pages/optimization/CampaignOptimizerPage'));

// Client Management (agency mode)
const ClientManagementPage = lazy(() => import('./pages/agency/ClientManagementPage'));

// White-Label Reports (agency mode)
const WhiteLabelReportsPage = lazy(() => import('./pages/agency/WhiteLabelReportsPage'));

// Ecommerce Intelligence
const EcommerceIntelligencePage = lazy(() => import('./pages/ecommerce/EcommerceIntelligencePage'));

// Attribution Trust
const AttributionTrustPage = lazy(() => import('./pages/AttributionTrustPage'));

// Attribution Pixel Setup
const AttributionPixelPage = lazy(() => import('./pages/AttributionPixelPage'));

// AI Decision Log
const AIDecisionLogPage = lazy(() => import('./pages/AIDecisionLogPage'));

// Sales Workspace (internal — admin/sales only)
const SalesWorkspacePage = lazy(() => import('./pages/SalesWorkspacePage'));

// Admin Dashboard (internal — admin only)
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));

// Maintenance page (shown on 503 maintenance responses)
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));

// Benchmark Dashboard
const BenchmarkDashboardPage = lazy(() => import('./pages/benchmarks/BenchmarkDashboardPage'));

// System health (protected)
const SystemHealthPage = lazy(() => import('./pages/system-health/SystemHealthPage'));

// Password reset pages (public)
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Email verification pages
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const CheckEmailPage = lazy(() => import('./pages/CheckEmailPage'));

// Social auth pages
const SocialAuthSuccessPage = lazy(() => import('./pages/SocialAuthSuccessPage'));
const SocialAuthErrorPage = lazy(() => import('./pages/SocialAuthErrorPage'));

// Organic Social Publishing
const SocialQueuePage = lazy(() => import('./pages/social/SocialQueuePage'));
const SocialAccountsPage = lazy(() => import('./pages/social/SocialAccountsPage'));

const SecurityMarketingPage = lazy(() => import('./pages/marketing/SecurityPage'));
const PrivacyPage = lazy(() => import('./pages/marketing/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/marketing/TermsPage'));
const ContactPage = lazy(() => import('./pages/marketing/ContactPage'));
const ChangelogPage = lazy(() => import('./pages/marketing/ChangelogPage'));

// Layout component
const AppLayout = lazy(() => import('./components/common/AppLayout'));

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30 seconds
    },
  },
});

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component (redirects if authenticated, but lets demo users through
// so the DemoBanner's "Sign up for free" CTA can reach /login and /signup)
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading, isDemoAccount } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated && !isDemoAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Home route — landing page for guests, redirect to /dashboard for authed users
function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

// Accessibility testing in development
function useAccessibilityTesting() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('@axe-core/react').then(({ default: axe }) => {
        import('react-dom').then((ReactDOM) => {
          axe(React, ReactDOM, 1000);
        });
      });
    }
  }, []);
}

// Main App Component
function AppContent() {
  // Enable accessibility testing in development
  useAccessibilityTesting();

  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Routes>
        {/* Public auth routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginForm />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignupForm />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route path="/demo" element={<DemoBootstrap />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/auth/social/success" element={<SocialAuthSuccessPage />} />
        <Route path="/auth/social/error" element={<SocialAuthErrorPage />} />

        {/* Marketing shell — always accessible (no auth gate) */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security" element={<SecurityMarketingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />

          {/* SEO calculator pages */}
          <Route path="/calculators" element={<CalculatorsIndex />} />
          <Route path="/calculate-roas"            element={<CalculatorPage />} />
          <Route path="/calculate-cpa"             element={<CalculatorPage />} />
          <Route path="/calculate-ad-budget"       element={<CalculatorPage />} />
          <Route path="/calculate-break-even-roas" element={<CalculatorPage />} />
          <Route path="/calculate-ctr"             element={<CalculatorPage />} />
          <Route path="/calculate-conversion-rate" element={<CalculatorPage />} />

          {/* Competitor comparison pages */}
          <Route path="/adflow-vs"            element={<ComparisonIndex />} />
          <Route path="/adflow-vs-opteo"      element={<ComparisonPage />} />
          <Route path="/adflow-vs-trueclicks" element={<ComparisonPage />} />
          <Route path="/adflow-vs-optmyzr"    element={<ComparisonPage />} />
          <Route path="/adflow-vs-wordstream" element={<ComparisonPage />} />

          {/* Free Google Ads audit tool */}
          <Route path="/audit"         element={<AuditPage />} />
          <Route path="/audit/results" element={<AuditResultsPage />} />

          {/* SEO feature landing pages */}
          <Route path="/features"                       element={<FeaturesIndex />} />
          <Route path="/google-ads-automation"          element={<SeoLandingPage />} />
          <Route path="/ppc-automation-software"        element={<SeoLandingPage />} />
          <Route path="/google-ads-budget-optimization" element={<SeoLandingPage />} />
          <Route path="/ppc-management-software"        element={<SeoLandingPage />} />
          <Route path="/ads-automation-tool"            element={<SeoLandingPage />} />

          {/* Public benchmark pages */}
          <Route path="/google-ads-benchmarks"          element={<GoogleAdsBenchmarksPage />} />
          <Route path="/google-ads-roas-benchmarks"     element={<RoasBenchmarksPage />} />
          <Route path="/google-ads-cpa-benchmarks"      element={<CpaBenchmarksPage />} />

          {/* Shared report page */}
          <Route path="/report/share/:token" element={<SharedReportPage />} />

          {/* Case studies */}
          <Route path="/case-studies" element={<CaseStudiesPage />} />
        </Route>

        {/* Protected app shell — pathless layout route (no path attr = applies to children's paths) */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:campaignId" element={<CampaignDetail />} />

          {/* /analytics had no component — redirect to dashboard */}
          <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
          <Route path="/settings/*" element={<Settings />} />

          {/* AI tools */}
          <Route path="/ai/ad-copy" element={<AdCopyGenerator />} />
          <Route path="/ai/anomalies" element={<FeatureGate feature="advanced_analytics" fallback={<Navigate to="/dashboard" replace />}><AnomalyDetection /></FeatureGate>} />

          {/* Reports & Analytics */}
          <Route path="/reports/builder" element={<ReportBuilder />} />

          {/* Integrations */}
          <Route path="/integrations" element={<IntegrationsHub />} />

          {/* OAuth callbacks — Meta Ads & TikTok Ads */}
          <Route path="/integrations/callback/:provider" element={<OAuthCallbackPage />} />

          {/* Security Settings */}
          <Route path="/settings/security" element={<SecuritySettings />} />

          {/* Automation — feature-flagged for launch MVP */}
          <Route path="/automation" element={<FeatureGate feature="automation_rules" fallback={<Navigate to="/dashboard" replace />}><RuleList /></FeatureGate>} />

          {/* Weekly report — feature-flagged for launch MVP */}
          <Route path="/weekly-report" element={<FeatureGate feature="advanced_analytics" fallback={<Navigate to="/dashboard" replace />}><WeeklyReportPage /></FeatureGate>} />

          {/* Multi-channel unified dashboard — consolidated into main dashboard */}
          <Route path="/channels/dashboard" element={<Navigate to="/dashboard" replace />} />

          {/* Referral program — feature-flagged for launch MVP */}
          <Route path="/referrals" element={<FeatureGate feature="referral_program" fallback={<Navigate to="/dashboard" replace />}><ReferralPage /></FeatureGate>} />

          {/* Agency partner program — feature-flagged for launch MVP */}
          <Route path="/partners"       element={<FeatureGate feature="partner_program" fallback={<Navigate to="/dashboard" replace />}><PartnerDashboardPage /></FeatureGate>} />
          <Route path="/partners/apply" element={<FeatureGate feature="partner_program" fallback={<Navigate to="/dashboard" replace />}><PartnerApplyPage /></FeatureGate>} />

          {/* Billing settings */}
          <Route path="/settings/billing" element={<BillingPage />} />

          {/* Team management */}
          <Route path="/settings/team" element={<TeamPage />} />

          {/* Optimization score */}
          <Route path="/optimization" element={<OptimizationScorePage />} />

          {/* Campaign Optimizer */}
          <Route path="/campaign-optimizer" element={<CampaignOptimizerPage />} />

          {/* Client Management (agency mode) */}
          <Route path="/clients" element={<ClientManagementPage />} />

          {/* White-Label Reports (agency mode) */}
          <Route path="/white-label-reports" element={<WhiteLabelReportsPage />} />

          {/* Ecommerce Intelligence */}
          <Route path="/ecommerce" element={<EcommerceIntelligencePage />} />

          {/* Attribution Trust */}
          <Route path="/attribution/trust" element={<AttributionTrustPage />} />

          {/* Attribution Pixel Setup */}
          <Route path="/attribution/pixel" element={<AttributionPixelPage />} />

          {/* AI Decision Log */}
          <Route path="/ai/decisions" element={<AIDecisionLogPage />} />

          {/* Benchmark Dashboard */}
          <Route path="/benchmark-dashboard" element={<BenchmarkDashboardPage />} />

          {/* System health — feature-flagged for launch MVP */}
          <Route path="/system-health" element={<FeatureGate feature="system_health" fallback={<Navigate to="/dashboard" replace />}><SystemHealthPage /></FeatureGate>} />

          {/* Sales Workspace — internal, admin/sales role only */}
          <Route path="/sales/workspace" element={<SalesWorkspacePage />} />

          {/* Admin Dashboard — internal, admin role only */}
          <Route path="/admin" element={<AdminDashboardPage />} />

          {/* Organic social publishing */}
          <Route path="/social" element={<FeatureGate feature="social_publishing" fallback={<Navigate to="/dashboard" replace />}><SocialQueuePage /></FeatureGate>} />
          <Route path="/social/accounts" element={<FeatureGate feature="social_publishing" fallback={<Navigate to="/dashboard" replace />}><SocialAccountsPage /></FeatureGate>} />
        </Route>

        {/* Onboarding — full-screen, protected but outside AppLayout */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />

        {/* Maintenance page — public, no auth required */}
        <Route path="/maintenance" element={<MaintenancePage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Lazy-load React Query Devtools only in development
const ReactQueryDevtoolsLazy = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  }))
);

function ReactQueryDevtoolsLoader() {
  if (process.env.NODE_ENV !== 'development') return null;
  return (
    <Suspense fallback={null}>
      <ReactQueryDevtoolsLazy initialIsOpen={false} />
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <OrgProvider>
              <AccountProvider>
              <UpgradeModalProvider>
              <ErrorBoundary>
                <StagingBanner />
                <AppContent />
                <DemoBanner />
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                className: '',
                style: {
                  background: '#1f2937',
                  color: '#f3f4f6',
                  border: '1px solid #374151',
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#f3f4f6',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#f3f4f6',
                  },
                },
              }}
            />
              </ErrorBoundary>
              </UpgradeModalProvider>
              </AccountProvider>
              </OrgProvider>
            </AuthProvider>
          </BrowserRouter>

          {/* React Query Devtools — lazy-loaded, dev only */}
          <ReactQueryDevtoolsLoader />
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
